/* eslint-disable import/no-cycle */
import axios from 'axios';
import {
  isDemoModeActive, isSandboxAccount, activeDemoOverrides, addDemoItem,
} from '@/composables/DemoMode';

export enum NestApiType {
  PAYMENTS = 'payments',
  ESR = 'esr',
  CLIENT_API = 'client_api',
  AUTH = 'auth',
  IMPORT = 'import',
  MESSAGING = 'messaging',
  DEFAULT = 'default',
}

const getPrefix = (typeApi: NestApiType) => {
  let prefix;

  switch (typeApi) {
    case NestApiType.PAYMENTS:
      prefix = process.env.VUE_APP_NESTJS_PAYMENT_API_URL;
      break;
    case NestApiType.ESR:
      prefix = process.env.VUE_APP_NESTJS_ESR_API_URL;
      break;
    case NestApiType.CLIENT_API:
      prefix = '/shopimind-api';
      break;
    case NestApiType.AUTH:
      prefix = '/shopimind-auth';
      break;
    case NestApiType.IMPORT:
      prefix = process.env.VUE_APP_NESTJS_IMPORT_URL;
      break;
    case NestApiType.MESSAGING:
      prefix = process.env.VUE_APP_NESTJS_MESSAGING_API_URL;
      break;
    case NestApiType.DEFAULT:
    default:
      prefix = process.env.VUE_APP_NESTJS_API_URL;
      break;
  }

  return prefix;
};

export const toQueryString = (obj: any, prefix = ''): string => {
  const queryParts: string[] = [];

  Object.entries(obj).forEach(([key, value]) => {
    const prefixedKey = prefix ? `${prefix}[${key}]` : key;

    if (Array.isArray(value)) {
      value.forEach((v, i) => {
        queryParts.push(`${encodeURIComponent(prefixedKey)}[${i}]=${encodeURIComponent(v)}`);
      });
    } else if (typeof value === 'object' && value !== null) {
      queryParts.push(toQueryString(value, prefixedKey));
    } else {
      queryParts.push(`${encodeURIComponent(prefixedKey)}=${encodeURIComponent(String(value))}`);
    }
  });

  return queryParts.join('&');
};

const DEV_JWT_STORAGE_KEY = 'spm_dev_jwt';

export const isDevBearerMode = (): boolean => {
  if (process.env.NODE_ENV === 'production') return false;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
};

/**
 * DEV-ONLY helper. In production returns {} so GraphQL clients and raw fetches
 * keep their previous behavior (no Authorization header; rely on same-origin cookie).
 */
export const getAuthHeaders = (): Record<string, string> => {
  if (!isDevBearerMode()) return {};
  const token = getJwtTokenFromCookie();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * In dev bearer mode, reads localStorage first. In production, only reads the cookie
 * (preserves the exact pre-existing behavior used by nestCall).
 */
export const getJwtTokenFromCookie = (): string | null => {
  if (isDevBearerMode()) {
    const devToken = window.localStorage.getItem(DEV_JWT_STORAGE_KEY);
    if (devToken) return devToken;
  }

  const cookies = document.cookie.split(';');
  const apiTokenCookie = cookies
    .map((cookie) => cookie.trim().split('='))
    .find(([name]) => name === 'api_token_v2');

  return apiTokenCookie ? decodeURIComponent(apiTokenCookie[1]) : null;
};

export const setDevJwtToken = (token: string): void => {
  if (!isDevBearerMode()) return;
  window.localStorage.setItem(DEV_JWT_STORAGE_KEY, token);
};

export const clearDevJwtToken = (): void => {
  if (!isDevBearerMode()) return;
  window.localStorage.removeItem(DEV_JWT_STORAGE_KEY);
};

const AUTH_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

const getAuthCookieDomain = (): string | null => {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return null;
  const parts = host.split('.');
  if (parts.length < 2) return null;
  return `.${parts.slice(-2).join('.')}`;
};

export const writeAuthCookies = (
  token: string,
  shopVersion: 'v4' | 'v5',
  rememberMe = true,
): void => {
  if (typeof document === 'undefined') return;
  const domain = getAuthCookieDomain();
  const domainPart = domain ? `; Domain=${domain}` : '';
  const securePart = window.location.protocol === 'https:' ? '; Secure' : '';
  const agePart = rememberMe ? `; Max-Age=${AUTH_COOKIE_MAX_AGE_SECONDS}` : '';
  const base = `Path=/${agePart}; SameSite=Strict${securePart}${domainPart}`;
  document.cookie = `api_token_v2=${encodeURIComponent(token)}; ${base}`;
  document.cookie = `spm_platform_version=${shopVersion}; ${base}`;
};

export const clearPlatformVersionCookie = (): void => {
  if (typeof document === 'undefined') return;
  const domain = getAuthCookieDomain();
  const securePart = window.location.protocol === 'https:' ? '; Secure' : '';
  const expire = 'Path=/; Max-Age=0; SameSite=Strict';
  document.cookie = `spm_platform_version=; ${expire}${securePart}`;
  if (domain) {
    document.cookie = `spm_platform_version=; ${expire}${securePart}; Domain=${domain}`;
  }
};

export const clearAuthCookies = (): void => {
  if (typeof document === 'undefined') return;
  const domain = getAuthCookieDomain();
  const securePart = window.location.protocol === 'https:' ? '; Secure' : '';
  const expire = 'Path=/; Max-Age=0; SameSite=Strict';
  document.cookie = `api_token_v2=; ${expire}${securePart}`;
  if (domain) {
    document.cookie = `api_token_v2=; ${expire}${securePart}; Domain=${domain}`;
  }
  clearPlatformVersionCookie();
};

/**
 * Generic secured method to communicate with NestJS using JWT authentication
 */
export const nestCall = async (
  method: 'get' | 'post' | 'patch' | 'put' | 'delete',
  typeApi: NestApiType,
  path: string,
  headers: any = {},
  query = '',
  data?: any,
  secure = true,
  params = {},
  axiosOptions: Record<string, any> = {},
) => {
  let result = null;
  const prefix = getPrefix(typeApi);
  const url = `${prefix}${path}${query}`;

  const formattedHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...headers,
  };

  // Add the JWT token in the header Authorization if secure mode is enabled
  if (secure) {
    const jwtToken = getJwtTokenFromCookie();
    if (jwtToken) {
      formattedHeaders.Authorization = `Bearer ${jwtToken}`;
    }
  }

  // Inject demo mode headers when sandbox account has demo mode active
  if (isSandboxAccount.value && isDemoModeActive.value) {
    formattedHeaders['X-Demo-Mode'] = 'true';
    const overrides = activeDemoOverrides.value;
    if (overrides) {
      formattedHeaders['X-Demo-Shop-Overrides'] = JSON.stringify(overrides);
    }
  }

  try {
    let process;

    const requestConfig: Record<string, any> = {
      headers: formattedHeaders,
      withCredentials: secure,
      ...axiosOptions,
    };

    if (params && Object.keys(params).length > 0) {
      requestConfig.params = {
        ...(axiosOptions?.params ?? {}),
        ...params,
      };
    }

    // Handle different HTTP methods
    switch (method.toLowerCase()) {
      case 'get':
        process = await axios.get(url, requestConfig);
        break;
      case 'post':
        process = await axios.post(url, data, requestConfig);
        break;
      case 'patch':
        process = await axios.patch(url, data, requestConfig);
        break;
      case 'put':
        process = await axios.put(url, data, requestConfig);
        break;
      case 'delete':
        // axios.delete signature is (url, config). To send a JSON body the
        // payload must be passed as config.data — otherwise it's dropped.
        process = await axios.delete(url, {
          ...requestConfig,
          ...(data !== undefined ? { data } : {}),
        });
        break;
      default:
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    if (process && process.data) {
      // If responseType is blob, return both data and headers
      if (axiosOptions.responseType === 'blob') {
        result = {
          data: process.data,
          headers: process.headers,
        };
      } else {
        result = process.data;
      }
    }
  } catch (error: any) {
    result = null;

    // Specific handling of authentication errors (only in secure mode)
    if (error.response) {
      const { status, data: errorData } = error.response;

      // Authentication/authorization errors (only relevant in secure mode)
      if (secure && (status === 401 || status === 403)) {
        return {
          error: {
            ...errorData,
            isAuthError: true,
            status,
          },
        };
      }

      return { error: errorData };
    }

    if (error.message) {
      return { error: error.message };
    }
  }

  // In demo mode, auto-register newly created items so they appear in filtered lists
  if (isDemoModeActive.value && method === 'post' && result) {
    const id = result?.data?.campaignId || result?.data?.id || result?.data?.id_template;
    if (id) {
      if (path.includes('/campaigns/automations')) addDemoItem('automation', Number(id));
      else if (path.includes('/campaigns/bulk')) addDemoItem('bulk', Number(id));
      else if (path.includes('/templates')) addDemoItem('template', Number(id));
    }
  }

  return result;
};
